import React, { Suspense, ComponentType } from 'react'
// import React, { lazy, Suspense } from 'react'
import { Spin } from 'antd'
import {
  Route,
  Navigate,
  Routes,
  useLocation,
  useParams,
  useNavigate,
  Location as RouterLocation,
  NavigateFunction,
} from 'react-router'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import { connect } from 'react-redux'
import { history as globalHistory } from 'index'
import { getDefaultPage } from 'env'
import { Loading3QuartersOutlined } from '@ant-design/icons'

import { parseSearchParam, processNParam } from 'utils'
import { isDevelop } from 'vars'

import Layout from 'layouts'

// dev/local 전용 EmailView 확인 페이지 (OFM-233 Phase 8). isDevelop 이 false 인
// 프로덕션 빌드에서는 삼항의 import() 가 dead-code 로 제거되어 청크가 생성되지 않는다.
const DevMailView = isDevelop ? React.lazy(() => import('./pages/dev/DevMailView')) : null

// dev/local 전용 biz 로그인 실패 모달 UI 테스트 페이지 (OMD-27). 프로덕션 빌드에서는 dead-code 제거.
const BizErrorModalTest = isDevelop
  ? React.lazy(() => import('./pages/dev/BizErrorModalTest'))
  : null

// Custom Location type from history.ts
type CustomLocation = Location

interface WithRouterOptions {
  navigate?: boolean
}

interface Match {
  params: Record<string, string | undefined>
  path: string
}

interface WithRouterProps {
  location: RouterLocation
  navigate: NavigateFunction
  history: typeof globalHistory
  match: Match
}

// Export RouteComponentProps as an alias to WithRouterProps for compatibility
export type RouteComponentProps<Params = Record<string, string | undefined>> = WithRouterProps & {
  match: Match & {
    params: Params
  }
}

export function withRouter<P extends object>(
  Component: ComponentType<P & Partial<WithRouterProps>>,
  options?: WithRouterOptions,
) {
  return (props: P) => {
    const newProps = {
      ...props,
    } as P & WithRouterProps

    newProps.location = useLocation()
    newProps.navigate = useNavigate()
    newProps.history = globalHistory
    newProps.match = {
      params: useParams(),
      path: newProps.location.pathname,
    }

    if (options != null && options.navigate === true) {
      newProps.navigate = useNavigate()
    }

    return <Component {...newProps} />
  }
}

// const config = {
//   fallback: <Spin />,
// }

interface RouteData {
  path: string
  Component: ComponentType<any>
  exact?: boolean
}

interface MenuState {
  initialized: boolean
  routerData: RouteData[]
}

interface UserState {
  serverType?: string
}

interface AppSettingsState {
  routerAnimation: string
}

interface MapStateToPropsReturn {
  initialized: boolean
  myRoutes: RouteData[]
  routerAnimation: string
  serverType?: string
}

const mapStateToProps = ({
  menu,
  user,
  appsettings,
}: {
  menu: MenuState
  user: UserState
  appsettings: AppSettingsState
}): MapStateToPropsReturn => ({
  initialized: menu.initialized,
  myRoutes: menu.routerData,
  routerAnimation: appsettings.routerAnimation,
  serverType: user.serverType,
})

interface RouterProps extends MapStateToPropsReturn {
  history: typeof globalHistory
  location: RouterLocation
}

interface UnlistenCallback {
  (): void
}

class RouterComponent extends React.Component<RouterProps> {
  unsubscribeFromHistory: UnlistenCallback | null = null

  constructor(props: RouterProps) {
    super(props)
    // await localforage.setItem('app.router.location', '');

    const { history } = props
    this.unsubscribeFromHistory = history.listen(this.handleLocationChange)
    this.handleLocationChange(history.location as any)
  }

  // componentDidMount() {
  // }

  componentWillUnmount() {
    if (this.unsubscribeFromHistory) this.unsubscribeFromHistory()
  }

  handleLocationChange = (e: CustomLocation) => {
    const { history } = this.props
    console.log('ROUTE CHANGED', e, history)

    parseSearchParam()
    processNParam()
  }

  // const Router = ({ history, routerAnimation, serverType }) => {
  render = () => {
    const { location, initialized, myRoutes, routerAnimation } = this.props

    const defaultPage = getDefaultPage()

    // dev 전용 EmailView 확인 페이지: 메뉴 초기화/로그인 이전에도 열리도록 init 가드보다
    // 먼저 평가한다. Layout(메뉴 의존) 밖에서 단독 렌더한다.
    if (isDevelop && DevMailView != null && location.pathname === '/dev/mailview') {
      return (
        <Suspense
          fallback={
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
              }}
            >
              <Spin indicator={<Loading3QuartersOutlined spin />} />
            </div>
          }
        >
          <DevMailView />
        </Suspense>
      )
    }

    // dev 전용 biz 로그인 실패 모달 테스트 페이지 (OMD-27). 로그인/메뉴 초기화 이전에도 열리도록 init 가드보다 먼저 평가.
    if (isDevelop && BizErrorModalTest != null && location.pathname === '/test') {
      return (
        <Suspense
          fallback={
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
              }}
            >
              <Spin indicator={<Loading3QuartersOutlined spin />} />
            </div>
          }
        >
          <BizErrorModalTest />
        </Suspense>
      )
    }

    if (initialized === false && location.pathname.indexOf('/auth/') !== 0) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <Spin indicator={<Loading3QuartersOutlined spin />} />
        </div>
      )
    }

    return (
      <Layout location={location}>
        <SwitchTransition>
          <CSSTransition
            key={location.pathname}
            appear
            classNames={routerAnimation}
            timeout={routerAnimation === 'none' ? 0 : 300}
          >
            <Routes location={location}>
              <Route path="/" element={<Navigate to={defaultPage} />} />
              {myRoutes.map(({ path, Component }) => (
                <Route
                  path={path}
                  key={path}
                  element={
                    <Suspense>
                      <div className={routerAnimation}>
                        <Component />
                      </div>
                    </Suspense>
                  }
                />
              ))}
              <Route path="*" element={<Navigate to="/auth/404" />} />
            </Routes>
          </CSSTransition>
        </SwitchTransition>
      </Layout>
    )
  }
}

const ConnectedRouter = connect(mapStateToProps)(RouterComponent)
export default withRouter(ConnectedRouter as any)
